import {useCallback, useEffect, useRef, useState} from "react";
import {variantDataRetrieverMapFilters} from "./filtersDataRetriever";
import {variantRawDataProcessorMapFilters} from "./filtersRawDataProcessor";
import {Drawer, IconButton} from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import {Close} from "@mui/icons-material";
import {StyledButtonOutlined} from "../../utils/uiUtils";
import {hasValue, toBytes} from "../../utils/utils";
import {OptionsEngine} from "../OptionsEngine/OptionsEngine";


/**
 * Filters — Drawer shell for the OptionsEngine.
 *
 * Owns: container (Drawer), header, footer, data retrieval pipeline, output transformation.
 * Delegates: form state, field rendering, grouping, dependencies → OptionsEngine.
 *
 * @param {Array} filtersStructure - Config array (items with Key, Label, Type, CategoryName, Options)
 * @param {string} variant - Data retrieval variant key
 * @param {Object} additionalData - Optional additional data for the processor
 * @param {Function} filterDialogCloseFn - Closes the drawer
 * @param {boolean} isDialogOpen - Controls drawer visibility
 * @param {Function} setFilterDataFn - Receives processed filter data on submit
 */
export const Filters = ({
                            filtersStructure,
                            variant,
                            additionalData = undefined,
                            filterDialogCloseFn,
                            isDialogOpen,
                            setFilterDataFn
                        }) => {
    // --------------- Data Retrieval and Processing ---------------
    const [rawSupportData, setRawSupportData] = useState({});
    const [processedSupportData, setProcessedSupportData] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const dataRetrieverFn = variantDataRetrieverMapFilters[variant] ?? variantDataRetrieverMapFilters.defValue;
        dataRetrieverFn(setRawSupportData, setLoading);
    }, [variant]);

    useEffect(() => {
        const processorFn = variantRawDataProcessorMapFilters[variant] ?? variantRawDataProcessorMapFilters.defValue;
        processorFn(setProcessedSupportData, rawSupportData, additionalData);
    }, [rawSupportData, additionalData]);

    // --------------- OptionsEngine ref + validity ---------------
    const engineRef = useRef();
    const [isValid, setIsValid] = useState(true);

    // --------------- Output transformation ---------------
    const handleSubmit = useCallback((nestedData) => {
        // Flatten nested output from OptionsEngine back to a flat dict
        const data = {};
        Object.values(nestedData).forEach(group => {
            Object.assign(data, group);
        });

        const filterData = {};

        filtersStructure.forEach(item => {
            const key = item.Key;
            const filterType = item.Type;
            const filterValue = data[key];
            const sizeUnitValue = ['sizeInput'].includes(filterType) ? data[`${key}_size_unit`] || 'B' : undefined;
            const _hasValue = hasValue(filterValue);

            switch (filterType) {
                case "toggleInput":
                    filterData[key] = filterValue;
                    break;
                case "dateInput":
                    if (!_hasValue) break;
                    const date = filterValue instanceof Date ? filterValue : new Date(filterValue);
                    filterData[key] = date.toISOString();
                    break;
                case "multiselect":
                    if (!_hasValue) break;
                    filterData[key] = Array.isArray(filterValue) ? filterValue : [filterValue];
                    break;
                case "sizeInput":
                    if (!_hasValue) break;
                    filterData[key] = toBytes(filterValue, sizeUnitValue);
                    break;
                default:
                    if (!_hasValue) break;
                    filterData[key] = filterValue;
            }
        });

        setFilterDataFn(filterData);
    }, [filtersStructure, setFilterDataFn]);

    const handleReset = () => {
        engineRef.current?.reset();
        setFilterDataFn({});
    };

    // --------------- Rendering ---------------
    return (
        <Drawer
            variant="persistent"
            anchor="right"
            open={isDialogOpen}
            PaperProps={{
                sx: {
                    top: "25%",
                    borderRadius: 2,
                    p: 2,
                    width: 400,
                    height: "70%",
                    overflowY: "auto",
                    backgroundColor: "#4E5152",
                    boxShadow: "-4px 4px 4px 0px #00000029",
                }
            }}>
            {loading ?
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                    }}>
                    <CircularProgress/>
                </Box> :
                <Grid container rowSpacing={1} alignItems="center">
                    <Grid item xs={11}>
                        <Typography variant="h6" sx={{color: "#FFFFFF"}} noWrap textAlign={'left'}> Filters </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <IconButton onClick={filterDialogCloseFn}>
                            <Close sx={{color: "#FFFFFF"}}/>
                        </IconButton>
                    </Grid>
                    <Grid
                        item xs={6}
                        container
                        justifyContent="flex-start"
                    >
                        <StyledButtonOutlined
                            variant="outlined"
                            onClick={() => engineRef.current?.submit()}
                            disabled={!isValid}
                        >
                            Apply Filters
                        </StyledButtonOutlined>
                    </Grid>
                    <Grid
                        item xs={6}
                        container
                        justifyContent="flex-end"
                    >
                        <StyledButtonOutlined
                            variant="outlined"
                            onClick={handleReset}
                            sx={{
                                borderColor: "#ffffff",
                                color: "#ffffff",
                            }}>
                            Reset Filters
                        </StyledButtonOutlined>
                    </Grid>
                    <Grid item xs={12}>
                        <OptionsEngine
                            ref={engineRef}
                            config={filtersStructure}
                            supportData={processedSupportData}
                            onSubmit={handleSubmit}
                            setIsValid={setIsValid}
                        />
                    </Grid>
                </Grid>
            }
        </Drawer>
    );
};
